import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CompensationStandard } from '../../../models/compensationStandard';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-compensation-standard-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './compensation-standard-list.component.html',
  styleUrl: './compensation-standard-list.component.scss'
})
export class CompensationStandardListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  compensationStandards = signal<CompensationStandard[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadCompensationStandards();
  }

  loadCompensationStandards(): void {
    this.spinnerService.show();
    this.lutService.getCompensationStandards().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortCompensationStandards(data);
        this.compensationStandards.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading compensation standards:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortCompensationStandards(compensationStandards: CompensationStandard[]): CompensationStandard[] {
    return [...compensationStandards].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateCompensationStandard(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(compensationStandard: CompensationStandard): void {
  }

  onEditCompensationStandard(compensationStandard: CompensationStandard): void {
  }

  onDeleteCompensationStandard(compensationStandard: CompensationStandard): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(compensationStandard.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalCompensationStandards = this.compensationStandards();

          // 1. Optimistically remove from local state immediately
          this.compensationStandards.update(compensationStandards => compensationStandards.filter(cs => cs.id !== compensationStandard.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteCompensationStandard(compensationStandard.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Compensation standard deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting compensation standard:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.compensationStandards.set(originalCompensationStandards);
              this.toast.error(`<strong>Failed to delete Compensation Standard:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: CompensationStandard, newName: string} | {item: CompensationStandard, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: CompensationStandard;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.compensationStandards.update(compensationStandards => {
      const updated = compensationStandards.map(cs => cs.id === event.item.id ? updatedItem : cs);
      return this.sortCompensationStandards(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateCompensationStandard(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Compensation standard updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.compensationStandards.update(compensationStandards => {
          const updated = compensationStandards.map(cs => cs.id === event.item.id ? response : cs);
          return this.sortCompensationStandards(updated);
        });
      },
      error: (error) => {
        console.error('Error updating compensation standard:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.compensationStandards.update(compensationStandards =>
          compensationStandards.map(cs => cs.id === event.item.id ? originalItem : cs)
        );
        this.toast.error(`<strong>Failed to update Compensation Standard:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newCompensationStandard: CompensationStandard = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.compensationStandards.update(compensationStandards => this.sortCompensationStandards([...compensationStandards, newCompensationStandard]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createCompensationStandard({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Compensation standard created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.compensationStandards.update(compensationStandards => {
          const updated = compensationStandards.map(cs => cs.id === tempId ? response : cs);
          return this.sortCompensationStandards(updated);
        });
      },
      error: (error) => {
        console.error('Error creating compensation standard:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.compensationStandards.update(compensationStandards =>
          compensationStandards.filter(cs => cs.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Compensation Standard:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
